import utils from '../../../../share/util/utils';
import vehicle from '../../../../share/util/vehicle';
import alert from '../../../../share/alert';
import { convertKeyForPidToDid } from '../../../../util/obd/pid';
import dmUtil, { WORKER, WORKER_EVENT, WORKER_TASK } from "../../../pages/dataMonitor/dataMonitorUtil";
import didDataConversion from '../../obd/individual/dataMonitor/didDataConversion';
import { getTranslateValue } from '../../obd/individual/dataMonitor/dataMonitorUtil';

const CSV = {
  LINE_BREAK: '\r\n',
  ECU_DECORATION: '################'
};
// Workerの待機間隔
const WORKER_SLEEP_INTERVAL = 500;
// Workerに一度に渡すデータ行数
const WORKER_ROW_LIMIT = 1000;
// CSVのデータが始まる列番号。0列目は時間、1列目はマーカー。
const DATA_COLUMN_NUM = 2;

/**
 * ファイルエクスポートボタン押下時の処理
 * @param {object} csvFileWriter CsvFileWriterクラスのインスタンス
 * @param {object} currentComponent 呼び出し元のコンポーネントのインスタンス
 * @param {object} dataMonitorComponent データモニタのコンポーネントのインスタンス
 * @param {object} projectDetails プロジェクト情報
 * @param {string} measurementId 計測ID
 * @param {string} ecuName ECU名
 * @param {function} errorDialogCallback エラーダイアログのボタン押下時のコールバック
 * @param {function} updateExportProgress 進捗更新のコールバック
 */
export async function exportCsv({ csvFileWriter, currentComponent, dataMonitorComponent, projectDetails, measurementId, ecuName, errorDialogCallback, updateExportProgress }) {
  let workerInstance = {};
  let csvData = [];
  let hasStopOrder = false;
  let workerError = false;

  /**
   * RestfulWorkerでエスケープしたデータのマージ
   * @param {array} measureData 除外していないデータ
   * @param {array} escapedDataPoints 除外データ
   * @returns {array} マージ済みデータ
   */
  function mergeData(measureData, escapedDataPoints = []) {
    const result = {};
    // 全idの集合
    const allIds = new Set([
      ...Object.keys(measureData),
      ...Object.keys(escapedDataPoints)
    ]);
    // 各idについてマージ＆ソート
    for (const id of allIds) {
      const measured = measureData[id] || [];
      const escaped = escapedDataPoints[id] || [];
      // マージしてtime順にソート
      result[id] = [...measured, ...escaped].sort((a, b) => a.time - b.time);
    }
    return result;
  }

  let _dataMonitorComponent = dataMonitorComponent;
  if (dataMonitorComponent.value) {
    _dataMonitorComponent = dataMonitorComponent.value;
  }
  try {
    // 子コンポーネントから必要な情報を収集
    const { conversionList, selectedFuncs, baseTime, escapedDataPoints } = _dataMonitorComponent;

    if (!baseTime) {
      // baserTimeがないデータは出力するデータがないため処理終了
      return;
    }

    // RestfulWorkerでエスケープしたデータのマージ
    const measureData = mergeData(
      _dataMonitorComponent.getMeasureData(),
      Object.values(escapedDataPoints)[0]
    );
    const markers = _dataMonitorComponent.getMarkers();
    const markerTimes = markers.map(marker => Object.values(marker)[0].markerTime).sort();

    // CSV用の文字列作成
    const descriptionStr = createCsvDescription();
    const headerStr = createCsvHeader(conversionList, selectedFuncs);

    // CSVのヘッダー部分の書き込み
    await csvFileWriter.write(descriptionStr + headerStr);

    // 計測項目ごとにデータポイント数が異なる可能性があるため、最も多いデータポイント数をCSVの行数にする
    const measurementCount = Object.keys(measureData).map(id => (measureData[id] ? measureData[id].length : 0)).reduce((a, b) => Math.max(a, b), -Infinity);
    let dataStr = '';
    const allCounts = Math.ceil(measurementCount / WORKER_ROW_LIMIT); // Workerに渡す回数の全体数
    // 大量データをWorkerのメッセージで送受信すると遅くなるため、一定行ごとに処理を行う
    for (let i = 0; i * WORKER_ROW_LIMIT < measurementCount; i++) {
      const startRow = i * WORKER_ROW_LIMIT;
      const partialDataStr = await createCsvData(conversionList, selectedFuncs, markerTimes, measureData, startRow, baseTime);
      dataStr = dataStr + partialDataStr;

      // Intdashからのデータの場合、Workerの実行回数を進捗とする
      updateExportProgress(allCounts, i + 1);
    }

    // CSVデータの書き込み
    await csvFileWriter.write(dataStr);
    alert.showInfo(currentComponent, null, currentComponent.$t('individual.data_monitor_csv_export_complete_message'));
  } finally {
    if (workerInstance instanceof Worker) {
      // Workerの停止と終了
      workerInstance.postMessage({ type: WORKER_TASK.STOP, worker: WORKER.MEASUREMENT_CSV });
      workerInstance.terminate();
      workerInstance = {};
    }
  }

  /**
   * CSVのヘッダー上部の計測時の情報の文字列を作成
   * @returns {string} CSVのヘッダー上部の内容の文字列
   */
  function createCsvDescription() {
    const targetMeasurement = projectDetails.measures[0].obd.find(measure => measure.measure_id === measurementId);

    const dataName = targetMeasurement.name || '';
    const memo = targetMeasurement.note || '';
    const vin = projectDetails.information.vin || '';
    const registrationNumber = (projectDetails.area_name ? projectDetails.area_name + ' ' : '')
      + (projectDetails.category ? projectDetails.category + ' ' : '')
      + (projectDetails.hiragana ? projectDetails.hiragana + ' ' : '')
      + (projectDetails.individual_number ? projectDetails.individual_number + ' ' : '');
    const vehicleName = projectDetails.information.vehicle_name || '';
    // 再生画面ではvehicleInfoのtypeが取得できないため、VINのフォーマットから判定
    const isVin = vehicle.isVinType(vin);
    const vehicleType = (isVin ? projectDetails.information.model_year : projectDetails.information.classification) || '';

    const dataNameDescription = `"${currentComponent.$t('individual.header_data_monitor_data_name')}",${utils.escapeForCsv(dataName)}`;
    const memoDescription = `"${currentComponent.$t('individual.label_memo')}",${utils.escapeForCsv(memo)}`;
    // vinの種類によって表示する行を変える
    const vinDescription = `"${currentComponent.$t('individual.header_data_monitor_vin')}",${isVin ? utils.escapeForCsv(vin) : utils.escapeForCsv('')}`;
    const frameNumberDescription = `"${currentComponent.$t('individual.header_data_monitor_frame_number')}",${isVin ? utils.escapeForCsv('') : utils.escapeForCsv(vin)}`;
    const registrationNumDescription = `"${currentComponent.$t('individual.header_data_monitor_registration_number')}",${utils.escapeForCsv(registrationNumber)}`;
    const vehicleNameDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_name')}",${utils.escapeForCsv(vehicleName)}`;
    const vehicleTypeDescription = `"${currentComponent.$t('individual.header_data_monitor_vehicle_type')}",${utils.escapeForCsv(vehicleType)}`;
    const ecuNameDescription = `"${CSV.ECU_DECORATION} ${ecuName} ${CSV.ECU_DECORATION}"`;

    return [
      dataNameDescription, memoDescription, vinDescription, frameNumberDescription, registrationNumDescription, vehicleNameDescription, vehicleTypeDescription, ecuNameDescription
    ].join(CSV.LINE_BREAK) + CSV.LINE_BREAK;
  }

  /**
   * CSVのヘッダーの文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} selectedFuncs 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @returns {string} CSVのヘッダーの内容の文字列
   */
  function createCsvHeader(conversionList, selectedFuncs) {
    let firstHeader = selectedFuncs.map((item) =>
      utils.escapeForCsv(currentComponent.$t(`pid.${convertKeyForPidToDid(item.data_monitor_id)}`))
    );
    firstHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark')}"`);
    firstHeader = firstHeader.join(',');

    let secondHeader = selectedFuncs.map((item) => {
      const unitId =
        conversionList[item.data_monitor_id]?.conversion?.unit ||
        didDataConversion.DEFAULT_UNIT_ID;
      return utils.escapeForCsv(currentComponent.$t(`obd_unit.` + unitId));
    });
    secondHeader.unshift(`"${currentComponent.$t('individual.header_data_monitor_time_unit')}"`, `"${currentComponent.$t('individual.header_data_monitor_mark_unit')}"`);
    secondHeader = secondHeader.join(',');

    return firstHeader + CSV.LINE_BREAK + secondHeader + CSV.LINE_BREAK;
  }

  /**
   * CSVのデータ部の文字列を作成
   * @param {object} conversionList 変換情報のリスト
   * @param {array} selectedFuncs 計測時の選択項目。CSVの列は選択項目の順番で出力する。
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {number} startRow 開始行
   * @param {number} baseTime 基準時刻
   * @returns {string} CSVのデータ部の内容の文字列
   */
  async function createCsvData(conversionList, selectedFuncs, markerTimes, measureData, startRow, baseTime) {
    const splittedMeasureData = {};
    for (const pid in measureData) {
      splittedMeasureData[pid] = measureData[pid].slice(startRow, startRow + WORKER_ROW_LIMIT);
    }

    // WorkerでCSVのベースデータ作成
    await startWorker(conversionList, markerTimes, splittedMeasureData, selectedFuncs, baseTime);

    const pidList = selectedFuncs.map(item => item.data_monitor_id);

    // CSV全体の行ごとのループ
    const translatedCsvData = csvData.map(row => {
      // 1行の列ごと（計測項目ごと）のループ
      const translatedRow = row.map((cell, index) => {
        if (index < DATA_COLUMN_NUM) {
          // データ部でない列はそのまま使用
          return cell;
        }
        const pid = pidList[index - DATA_COLUMN_NUM];
        // データ部の列は変換と翻訳処理を行う
        return utils.escapeForCsv(String(translate(cell, pid, conversionList[pid])));
      });
      return translatedRow.join(',') + CSV.LINE_BREAK;
    });

    // Workerから返された元のCSV情報はクリア
    csvData.splice(0);

    return translatedCsvData.join('');
  }

  /**
   * 値の翻訳と変換を実施する
   * @param {string} originValue 翻訳・変換対象の値
   * @param {string} pid 計測項目
   * @param {object} conversionInfo 変換情報 
   * @returns {string} 翻訳・変換後の値
   */
  function translate(originValue, pid, conversionInfo) {
    if (originValue && dmUtil.VALUE_TRANSLATE_TYPE.includes(conversionInfo.data_type)) {
      const translated = getTranslateValue(currentComponent, pid, originValue, conversionInfo);
      if (conversionInfo.data_type === 'decExStr') {
        return translated || didDataConversion.convertValueDataForHex(originValue, conversionInfo);
      } else {
        return translated;
      }
    } else {
      return originValue;
    }
  }

  /**
   * Web workerの開始処理
   * @param {object} conversionList 変換情報のリスト
   * @param {array} markerTimes マーカーの登録時間のリスト
   * @param {object} measureData 計測データ
   * @param {array} selectedFuncs 計測時の選択項目
   * @param {number} baseTime 基準時刻
   */
  async function startWorker(conversionList, markerTimes, measureData, selectedFuncs, baseTime) {
    // Workerを作成
    if (!(workerInstance instanceof Worker)) {
      // webpackChunkNameを設定した"obd-data-monitor-worker"が、ビルド時のファイル名に使用される
      workerInstance = new Worker(
          /* webpackChunkName: "obd-data-monitor-worker" */ new URL('../../../pages/obd/individual/dataMonitor/DataMonitorWorker.js', import.meta.url)
      );
    }

    const startMessage = {
      worker: WORKER.MEASUREMENT_CSV,
      type: WORKER_TASK.START,
      measureData: JSON.parse(JSON.stringify(measureData)),
      markerTimes: JSON.parse(JSON.stringify(markerTimes)),
      conversionList: JSON.parse(JSON.stringify(conversionList)),
      selectedFuncs: JSON.parse(JSON.stringify(selectedFuncs)),
      baseTime: baseTime
    };

    // Workerに処理開始メッセージを送信
    hasStopOrder = false;
    workerInstance.postMessage(startMessage);

    // Workerエラーフラグのリセット
    workerError = false;

    // Workerからメッセージを受信したときの処理
    workerInstance.onmessage = (event) => {
      csvWorkerListener(event);
    };

    // Workerの処理が完了するまで待機する
    while (!hasStopOrder) {
      // Workerでエラーがあった場合は待機を終了
      if (workerError) {
        break;
      }

      // 一定時間スリープ
      await utils.sleep(WORKER_SLEEP_INTERVAL);
    }
  }

  /**
   * csvWorkerからのメッセージ受信処理
   * @param {object} event 
   */
  function csvWorkerListener(event) {
    const data = event.data;

    // エラー受信の場合
    if (data.type === WORKER_EVENT.RUNTIME_ERROR) {
      // Workerでエラーあり
      workerError = true;
      // エラーダイアログ表示
      alert.showError(currentComponent, null, currentComponent.$t('message.common_systemError'), errorDialogCallback);
    }
    // 正常受信(CSVデータ)の場合
    else if (data.type === WORKER_EVENT.CSV_DATA) {
      csvData = csvData.concat(data.csvData);
    }
    // 正常受信(処理完了)の場合
    else if (data.type === WORKER_EVENT.SUCCESS_END) {
      hasStopOrder = true;
    }
  }
}
